/**
 * @file EditUserDialog.tsx
 * @brief Sheet form for editing a user via `PUT /api/admin/users/:id`.
 *
 * @details
 * - Pre-populates from the row; only changed fields are sent.
 * - Password is NOT edited here — see ChangePasswordDialog.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ClipboardList,
  CreditCard,
  LoaderCircle,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { TitlePicker } from "@/components/ui/TitlePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { UserSignupAnswers } from "./UserSignupAnswers";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import {
  useAssignCompanyEnterpriseSeat,
  useRevokeCompanyEnterpriseSeat,
} from "@/features/companies/api";
import { useIsOwner } from "@/shared/auth/meExtras";
import { useUpdateUser } from "../api";
import type { AdminUser } from "../schemas";
import { UpdateUserInputSchema, type UpdateUserInput } from "../schemas";

interface EditUserDialogProps {
  user: AdminUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditUserDialog({ user, open, onOpenChange }: EditUserDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  // Tabs (DES 01-10-2026: "yeh jo form hai isay tabs main karo too long").
  const [tab, setTab] = useState<EditUserTab>("profile");
  const updateUser = useUpdateUser();
  // B31: the Owner's row and (for anyone but the Owner) a Super Admin's
  // role, status, company and email cannot change; the server refuses
  // with OWNER_PROTECTED / OWNER_ONLY. Name, phone and title still can.
  const viewerIsOwner = useIsOwner();
  const accessLocked = user?.is_owner === true || (user?.role === "superadmin" && !viewerIsOwner);
  const lockedHint =
    user?.is_owner === true
      ? "The Owner account's role, status, company and email can't be changed here."
      : "Only the Owner can change a Super Admin's role, status, company or email.";

  const form = useForm<UpdateUserInput>({
    resolver: zodResolver(UpdateUserInputSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (open && user) {
      form.reset({
        email: user.email,
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        phone_number: user.phone_number ?? "",
        title: user.title ?? "",
        role: user.role,
        status: user.status,
        company_id: user.company_id ?? "",
      });
      setServerError(null);
      setTab("profile");
    }
  }, [open, user, form]);

  if (!user) return null;

  const onSubmit = form.handleSubmit(
    async (values) => {
      setServerError(null);
      const original = {
        email: user.email,
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        phone_number: user.phone_number ?? "",
        title: user.title ?? "",
        role: user.role,
        status: user.status,
        company_id: user.company_id ?? "",
      };

      const diff: UpdateUserInput = {};
      (Object.keys(values) as Array<keyof UpdateUserInput>).forEach((key) => {
        const next = values[key];
        const prev = original[key as keyof typeof original];
        if (next !== prev) {
          (diff as Record<string, unknown>)[key] = next === "" ? null : next;
        }
      });

      if (Object.keys(diff).length === 0) {
        toast({ title: "No changes to save" });
        onOpenChange(false);
        return;
      }

      try {
        await updateUser.mutateAsync({ id: user.id, input: diff });
        toast({
          variant: "success",
          title: "User updated",
          description: `${values.email ?? user.email} saved.`,
        });
        onOpenChange(false);
      } catch (err) {
        if (err instanceof ApiClientError) {
          setServerError(err.message);
        } else {
          setServerError("Unexpected error. Please try again.");
        }
      }
    },
    // A field in a hidden tab failed: open that tab so the error shows.
    (errs) => {
      const keys = Object.keys(errs);
      if (keys.some((k) => ["role", "status", "company_id"].includes(k))) setTab("access");
      if (
        keys.some((k) => ["first_name", "last_name", "email", "phone_number", "title"].includes(k))
      ) {
        setTab("profile");
      }
    },
  );

  const errors = form.formState.errors;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Opens like Cmd+K, not a side slide (DES 01-10-2026). */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>Edit user</FormSheetTitle>
              <FormSheetDescription>
                Profile, access, and company save with this form. Password is changed separately.
                Only changed fields are sent.
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetTabs tabs={EDIT_USER_TABS} value={tab} onChange={setTab} label="Edit user" />

            <FormSheetBody>
              {/* Every tab stays mounted, so a field's value and its error
                  survive switching tabs; hidden ones are display:none and
                  inert, so the sheet fits the open tab (DES 01-10-2026:
                  "now form height should be dynamic"). */}
              <div className="grid">
                <div {...formSheetTabPanel(tab, "profile")}>
                  <FormSheetSection
                    hideHeading
                    title="Profile"
                    subtitle="Identity and contact details."
                  >
                    {/* Title, first and last name in one row (DES 01-10-2026),
                        then email (two columns) and phone. */}
                    <FormSheetFields className="sm:grid-cols-3">
                      <FormSheetField>
                        <FormFieldGroup label="Title" error={errors.title?.message}>
                          <TitlePicker
                            value={form.watch("title") || null}
                            onChange={(label) => form.setValue("title", label ?? "")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="First name" error={errors.first_name?.message}>
                          <Input capitalizeFirst {...form.register("first_name")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Last name" error={errors.last_name?.message}>
                          <Input capitalizeFirst {...form.register("last_name")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField span={2}>
                        <FormFieldGroup
                          label="Email"
                          error={errors.email?.message}
                          hint={accessLocked ? lockedHint : "Only the person can change their email, from their own Profile."}
                        >
                          {/* Read-only for everyone (B34, 26-10-2026): the server never
                              took an email from this form, so an edit only looked saved. */}
                          <Input type="email" readOnly aria-readonly className="opacity-60" {...form.register("email")} />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Phone" error={errors.phone_number?.message}>
                          <Input {...form.register("phone_number")} />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>
                <div {...formSheetTabPanel(tab, "access")}>
                  {/* One section (DES 01-10-2026: "we dont need them in two
                      sections"): role, status and company together. */}
                  <FormSheetSection
                    hideHeading
                    title="Access"
                    subtitle={
                      accessLocked
                        ? lockedHint
                        : "Role controls permissions; status controls whether they can sign in. Attach a company for corporate billing, or clear it to leave the user independent."
                    }
                  >
                    <FormSheetFields>
                      <FormSheetField>
                        <FormFieldGroup label="Role" error={errors.role?.message}>
                          <Select
                            disabled={accessLocked}
                            value={form.watch("role")}
                            onValueChange={(v) =>
                              form.setValue("role", v as UpdateUserInput["role"])
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Company Admin</SelectItem>
                              {viewerIsOwner || user.role === "superadmin" ? (
                                <SelectItem value="superadmin">Super Admin</SelectItem>
                              ) : null}
                            </SelectContent>
                          </Select>
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup label="Status" error={errors.status?.message}>
                          <Select
                            disabled={accessLocked}
                            value={form.watch("status")}
                            onValueChange={(v) =>
                              form.setValue("status", v as UpdateUserInput["status"])
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="suspended">Suspended</SelectItem>
                              <SelectItem value="deleted">Deleted</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField span={2}>
                        <FormFieldGroup
                          label="Company"
                          hint="Clear to detach the user from any company."
                          error={errors.company_id?.message}
                        >
                          <CompanyPicker
                            disabled={accessLocked}
                            value={form.watch("company_id") || null}
                            onChange={(id) => form.setValue("company_id", id ?? "")}
                            placeholder="Select a company…"
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>
                <div {...formSheetTabPanel(tab, "signup")}>
                  {/* ⚠️ Read-only, like Subscription below it. The answers live
                  on the user row but only `GET /admin/users/:id` returns
                  them, so this block fetches by id rather than reading the
                  list row it was handed — see UserSignupAnswers. */}
                  <FormSheetSection
                    hideHeading
                    title="Signup"
                    subtitle="What this account told us when it registered."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <UserSignupAnswers userId={user.id} enabled={open} />
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>
                <div {...formSheetTabPanel(tab, "subscription")}>
                  <FormSheetSection
                    hideHeading
                    title="Subscription"
                    subtitle="Read-only seat summary. Enterprise seats can be toggled below when the company plan is active."
                  >
                    <FormSheetFields>
                      <FormSheetField span={2}>
                        <UserSubscriptionSummary user={user} />
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>
              </div>
              {serverError ? <FormSheetAlert>{serverError}</FormSheetAlert> : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={updateUser.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={updateUser.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {updateUser.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

function UserSubscriptionSummary({ user }: { user: AdminUser }) {
  const companyId = user.company_id || "";
  const assignSeat = useAssignCompanyEnterpriseSeat(companyId);
  const revokeSeat = useRevokeCompanyEnterpriseSeat(companyId);
  const seatBusy = assignSeat.isPending || revokeSeat.isPending;
  const [seated, setSeated] = useState(Boolean(user.enterprise_seated));

  useEffect(() => {
    setSeated(Boolean(user.enterprise_seated));
  }, [user.id, user.enterprise_seated]);

  const onToggleEnterpriseSeat = async () => {
    if (!companyId) return;
    try {
      if (seated) {
        await revokeSeat.mutateAsync(user.id);
        setSeated(false);
        toast({
          title: "Enterprise seat revoked",
          description: "This user is back on pay as you go for MI + Notes.",
        });
      } else {
        await assignSeat.mutateAsync(user.id);
        setSeated(true);
        toast({
          title: "Enterprise seat assigned",
          description: "This user now draws from the company shared pool.",
        });
      }
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Seat update failed";
      toast({
        title: "Seat update failed",
        description: msg,
        variant: "destructive",
      });
    }
  };

  if (user.enterprise_active) {
    const included = Number(user.enterprise_included_minutes ?? 0);
    const used = Number(user.enterprise_used_minutes ?? 0);
    const remaining = Math.max(0, included - used);
    const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">Company Enterprise shared pool</p>
          <span className="rounded bg-brand-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
            {seated ? "Seated" : "Not seated"}
          </span>
        </div>
        {seated && included > 0 ? (
          <>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="tabular-nums">
                <span className="font-semibold">{used.toLocaleString()}</span>
                <span className="text-muted-foreground">
                  {" "}
                  / {included.toLocaleString()} min used
                </span>
              </span>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                {remaining.toLocaleString()} left
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {seated
              ? "Seat assigned, but the shared pool has no included minutes configured."
              : "Company has Enterprise, but this user has no seat. MI + Notes stay pay as you go until assigned."}
          </p>
        )}
        {companyId ? (
          <Button
            type="button"
            variant={seated ? "outline" : "default"}
            size="sm"
            disabled={seatBusy}
            onClick={() => void onToggleEnterpriseSeat()}
          >
            {seatBusy ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Updating…
              </>
            ) : seated ? (
              "Revoke Enterprise seat"
            ) : (
              "Assign Enterprise seat"
            )}
          </Button>
        ) : null}
        {user.subscription_plan_code ? (
          <p className="text-xs text-muted-foreground">
            Personal {String(user.subscription_plan_code)} seat is overridden while Enterprise is
            seated.
          </p>
        ) : null}
      </div>
    );
  }

  if (!user.subscription_plan_code) {
    return (
      <p className="text-sm text-muted-foreground">
        Pay as you go: no monthly minute pool on this user.
      </p>
    );
  }

  const used = Number(user.subscription_used_minutes ?? 0);
  const included = Number(user.subscription_included_minutes ?? 0);
  const remaining = Number(user.subscription_remaining_minutes ?? Math.max(0, included - used));
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  const planLabel =
    String(user.subscription_plan_code).charAt(0).toUpperCase() +
    String(user.subscription_plan_code).slice(1);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Plan minutes (this period)</p>
        <span className="rounded bg-brand-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
          {planLabel}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="tabular-nums">
          <span className="font-semibold">{used.toLocaleString()}</span>
          <span className="text-muted-foreground"> / {included.toLocaleString()} min used</span>
        </span>
        <span className="text-xs font-medium tabular-nums text-muted-foreground">
          {remaining.toLocaleString()} left
        </span>
      </div>
      {user.subscription_period_end ? (
        <p className="text-xs text-muted-foreground">
          Until {new Date(user.subscription_period_end).toLocaleDateString()}
        </p>
      ) : null}
    </div>
  );
}

type EditUserTab = "profile" | "access" | "signup" | "subscription";

// Icons per tab (DES 01-10-2026: "tabs main icons dalo").
const EDIT_USER_TABS: FormSheetTab<EditUserTab>[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "access", label: "Access", icon: ShieldCheck },
  { id: "signup", label: "Signup", icon: ClipboardList },
  { id: "subscription", label: "Subscription", icon: CreditCard },
];
